/**
 * Turning permission codes into what a person could actually do, in plain words.
 *
 * A permission is "module.action.resource" — pos.create.pos, menu.update.menu.
 * An owner reads none of that. So each area gets a plain name and each action a
 * plain verb, and a role's permissions are grouped by area: "Menu: view, add,
 * edit" rather than four codes. An area a role holds in full collapses to "full
 * access", and a role that holds every area collapses to a single line — a
 * manager reading a list of jobs does not need the owner's twenty-two lines.
 */

/** The module, named the way the shop talks about it. */
const AREA = {
  auth: 'Staff accounts',
  branch: 'Branches',
  menu: 'Menu',
  recipe: 'Recipes',
  inventory: 'Stock',
  purchasing: 'Purchasing',
  supplier: 'Suppliers',
  grn: 'Goods received',
  pos: 'Till & orders',
  kitchen: 'Kitchen',
  accounting: 'Accounts',
  tax: 'Tax',
  delivery: 'Delivery',
  rider: 'Riders',
  crm: 'Customers',
  loyalty: 'Loyalty',
  expense: 'Expenses',
  cashier: 'Shifts & drawer',
  reports: 'Reports',
  settings: 'Settings',
  audit: 'Audit log',
  approval: 'Approvals',
};

/** The action, as a plain verb. */
const VERB = {
  create: 'add',
  read: 'view',
  update: 'edit',
  delete: 'remove',
  approve: 'approve',
  void: 'void',
  refund: 'refund',
  export: 'export',
};

/** Where the generic verb reads oddly, a phrase that fits the area. */
const PHRASE = {
  'pos.create': 'take orders',
  'pos.read': 'see orders',
  'pos.void': 'void a bill',
  'pos.refund': 'refund',
  'menu.create': 'add dishes',
  'menu.delete': 'remove dishes',
  'kitchen.read': 'see tickets',
  'kitchen.update': 'update tickets',
  'reports.read': 'see the reports',
  'reports.export': 'export reports',
  'auth.create': 'add staff',
  'settings.update': 'change settings',
};

// Read first, then the rest roughly by how much they change, so a line reads
// "view, add, edit" rather than an alphabetical jumble.
const ACTION_ORDER = ['read', 'create', 'update', 'delete', 'void', 'refund', 'approve', 'export'];
const ALL_ACTIONS = ACTION_ORDER.length;

/**
 * @param {string[]} permissions  e.g. ['pos.create.pos', 'menu.read.menu']
 * @returns {{ everything: boolean, areas: { name: string, doing: string }[] }}
 */
export function describeRole(permissions = []) {
  const byArea = new Map();
  for (const perm of permissions) {
    const [module, action] = String(perm).split('.');
    if (!AREA[module]) continue; // an area this screen has no name for is skipped
    if (!byArea.has(module)) byArea.set(module, new Set());
    byArea.get(module).add(action);
  }

  const areas = [...byArea.entries()]
    .map(([module, actions]) => {
      const full = actions.size >= ALL_ACTIONS;
      const doing = full
        ? 'full access'
        : ACTION_ORDER.filter((a) => actions.has(a))
            .map((a) => PHRASE[`${module}.${a}`] ?? VERB[a] ?? a)
            .join(', ');
      return { name: AREA[module], doing };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Everything, everywhere — the owner and the administrator. One line beats
  // listing every area as "full access".
  const everything = areas.length >= 12 && areas.every((a) => a.doing === 'full access');

  return { everything, areas };
}
